/**
 * /marketplace/wtb — Wanted-To-Buy Board.
 *
 * US breeders post WHAT they want to import. Foreign breeders browse
 * and pay $9 to reply with their kennel + photos + price.
 *
 * Armstrong is venue only — no escrow, no transport coordination.
 */
import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import PublicShell from "@/components/PublicShell";
import SeoHead from "@/components/SeoHead";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CompassIcon as Compass, PlusIcon as Plus, GlobeHemisphereWestIcon as GlobeHemisphereWest } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

export default function WtbBoard() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [breed, setBreed] = useState("");
  const [sex, setSex] = useState("any");
  const [ageBand, setAgeBand] = useState("any");
  const [breedingRights, setBreedingRights] = useState(false);
  const [postOpen, setPostOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (breed.trim()) params.set("breed", breed.trim());
      if (sex !== "any") params.set("sex", sex);
      if (ageBand !== "any") params.set("age_band", ageBand);
      if (breedingRights) params.set("breeding_rights", "true");
      const r = await api.get(`/wtb?${params.toString()}`);
      setPosts(r.data?.posts || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [breed, sex, ageBand, breedingRights]);

  const Shell = user ? AppShell : PublicShell;
  return (
    <Shell>
      <SeoHead
        title="Wanted-to-Buy Board · Armstrong K9 Registry"
        description="US breeders post the foundation stock they want to import. Foreign breeders pay $9 to reply with photos and pricing — no escrow, no liability."
        url={`${webOrigin()}/marketplace/wtb`}
        type="website"
      />
      <div className="max-w-5xl mx-auto px-4 py-10" data-testid="wtb-board">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
              Wanted-To-Buy Board
            </p>
            <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              US breeders importing foundation stock
            </h1>
            <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
              A venue-only board where US breeders post what they want to import
              (breed, sex, age, breeding rights, budget). Foreign breeders pay $9
              to unlock a single reply with photos + asking price — Armstrong
              does not hold money or coordinate transport.
            </p>
          </div>
          <Button
            onClick={() => {
              if (!user) { window.location.href = "/login?next=/marketplace/wtb"; return; }
              setPostOpen(true);
            }}
            className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-5"
            data-testid="wtb-post-btn"
          >
            <Plus size={14} className="mr-1.5" weight="bold" />
            Post a request
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">
          <aside className="bg-white border border-[#E5E5E5] rounded-xl p-5 space-y-4 h-fit">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">
                Breed
              </Label>
              <Input
                value={breed}
                onChange={(e) => setBreed(e.target.value)}
                placeholder="e.g. Cane Corso"
                className="mt-2"
                data-testid="wtb-filter-breed"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Sex</Label>
              <Select value={sex} onValueChange={setSex}>
                <SelectTrigger className="mt-2" data-testid="wtb-filter-sex"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Age</Label>
              <Select value={ageBand} onValueChange={setAgeBand}>
                <SelectTrigger className="mt-2" data-testid="wtb-filter-age"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any age</SelectItem>
                  <SelectItem value="puppy">Puppy</SelectItem>
                  <SelectItem value="junior">Junior</SelectItem>
                  <SelectItem value="adult">Proven adult</SelectItem>
                  <SelectItem value="retired">Retired stud / dam</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={breedingRights}
                onChange={(e) => setBreedingRights(e.target.checked)}
                className="mt-0.5 accent-[#C9A227]"
                data-testid="wtb-filter-breeding-rights"
              />
              <span>
                <span className="font-semibold">Breeding rights required</span>
              </span>
            </label>
          </aside>

          <section>
            {loading && <p className="text-sm text-[#6B6B6B]">Loading WTB posts…</p>}
            {!loading && posts.length === 0 && (
              <div
                className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-xl p-8 text-center"
                data-testid="wtb-empty"
              >
                <Compass size={28} weight="duotone" className="mx-auto text-gold mb-2" />
                <p className="font-semibold text-ink">No matching posts.</p>
                <p className="text-sm text-[#6B6B6B] mt-1">
                  Try clearing filters or be the first to post.
                </p>
              </div>
            )}
            <div className="space-y-3">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/marketplace/wtb/${p.id}`}
                  className="block bg-white border border-[#EAEAEA] rounded-xl p-5 hover:border-gold/55 hover:-translate-y-0.5 hover:shadow-gold-glow transition-all"
                  data-testid={`wtb-card-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-spectral text-lg tracking-tight leading-tight flex-1">
                      {p.title}
                    </h3>
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] flex items-center gap-1">
                      <GlobeHemisphereWest size={12} weight="duotone" />
                      {p.poster_country}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-[12px] text-[#3F3F3F]">
                    <span className="font-semibold">{p.breed}</span>
                    {p.sex && p.sex !== "any" && <span>· {p.sex}</span>}
                    {p.age_band && p.age_band !== "any" && <span>· {p.age_band}</span>}
                    {p.breeding_rights_required && (
                      <span className="bg-[#FBF7EC] border border-[#C9A227] rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A6A00]">
                        Breeding rights
                      </span>
                    )}
                    {p.budget_max_usd ? (
                      <span className="ml-auto font-tabular text-[12px] text-[#8A6A00] font-bold">
                        Budget up to ${Number(p.budget_max_usd).toLocaleString()}
                      </span>
                    ) : null}
                  </div>
                  {p.notes && (
                    <p className="text-[12.5px] text-[#6B6B6B] mt-2 line-clamp-2 leading-relaxed">
                      {p.notes}
                    </p>
                  )}
                  <div className="mt-3 text-[11px] text-[#9A9A9A]">
                    Posted by <strong className="text-ink">{p.poster_kennel}</strong>
                    {p.replies_count > 0 && (
                      <span className="ml-3 text-gold font-bold">
                        {p.replies_count} {p.replies_count === 1 ? "paid reply" : "paid replies"}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </div>
      <PostWtbDialog open={postOpen} onOpenChange={setPostOpen} onPosted={load} />
    </Shell>
  );
}

function PostWtbDialog({ open, onOpenChange, onPosted }) {
  const [title, setTitle] = useState("");
  const [breed, setBreed] = useState("");
  const [sex, setSex] = useState("any");
  const [ageBand, setAgeBand] = useState("any");
  const [color, setColor] = useState("");
  const [breedingRightsRequired, setBreedingRightsRequired] = useState(true);
  const [budgetMax, setBudgetMax] = useState("");
  const [notes, setNotes] = useState("");
  const [venueAck, setVenueAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!venueAck) { toast.error("Please acknowledge the venue-only terms."); return; }
    setSubmitting(true);
    try {
      await api.post("/me/wtb", {
        title: title.trim(),
        breed: breed.trim(),
        sex,
        age_band: ageBand,
        color: color.trim(),
        breeding_rights_required: breedingRightsRequired,
        budget_max_usd: budgetMax ? Number(budgetMax) || null : null,
        notes: notes.trim(),
        venue_acknowledged: true,
      });
      toast.success("Posted — foreign breeders can now reply.");
      onOpenChange(false);
      onPosted?.();
      setTitle(""); setBreed(""); setColor(""); setBudgetMax(""); setNotes("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="wtb-post-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl">Post a WTB request</DialogTitle>
          <DialogDescription>
            Foreign breeders pay $9 to send a single paid reply with their kennel,
            photos, and asking price. Armstrong is venue only — sales and
            transport happen entirely off-platform.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Looking for proven Cane Corso female, EU lines" className="mt-1.5" data-testid="wtb-post-title" maxLength={160}/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Breed</Label>
              <Input value={breed} onChange={(e) => setBreed(e.target.value)} placeholder="Cane Corso" className="mt-1.5" data-testid="wtb-post-breed"/>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Sex</Label>
              <Select value={sex} onValueChange={setSex}>
                <SelectTrigger className="mt-1.5" data-testid="wtb-post-sex"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Age</Label>
              <Select value={ageBand} onValueChange={setAgeBand}>
                <SelectTrigger className="mt-1.5" data-testid="wtb-post-age"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="puppy">Puppy</SelectItem>
                  <SelectItem value="junior">Junior</SelectItem>
                  <SelectItem value="adult">Proven adult</SelectItem>
                  <SelectItem value="retired">Retired stud / dam</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Budget USD (max)</Label>
              <Input type="number" value={budgetMax} onChange={(e) => setBudgetMax(e.target.value)} placeholder="e.g. 6000" className="mt-1.5" data-testid="wtb-post-budget"/>
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Color (optional)</Label>
            <Input value={color} onChange={(e) => setColor(e.target.value)} className="mt-1.5" data-testid="wtb-post-color"/>
          </div>
          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={breedingRightsRequired} onChange={(e) => setBreedingRightsRequired(e.target.checked)} className="mt-0.5 accent-[#C9A227]" data-testid="wtb-post-breeding-rights"/>
            <span>Must include full breeding rights</span>
          </label>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Health clearances, working titles, structure, temperament priorities..." className="mt-1.5 min-h-[100px]" maxLength={4000} data-testid="wtb-post-notes"/>
          </div>
          <label className="flex items-start gap-2 cursor-pointer text-[12px] leading-relaxed bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-3">
            <input type="checkbox" checked={venueAck} onChange={(e) => setVenueAck(e.target.checked)} className="mt-0.5 accent-[#C9A227]" data-testid="wtb-post-venue-ack"/>
            <span>
              <strong>Venue acknowledgement.</strong> I understand Armstrong K9
              Registry is the venue only. Armstrong does not screen sellers,
              dogs, or transport; does not hold money for the sale; and is not a
              party to any agreement made off-platform. Cross-border transport
              and any disputes are entirely my responsibility.
            </span>
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !title.trim() || !breed.trim() || !venueAck} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="wtb-post-submit">
            {submitting ? "Posting…" : "Publish to WTB Board"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
